import DOMPurify from "dompurify";
import React, { memo, useLayoutEffect, useMemo, useRef } from "react";
import colors from "app/colors";
import useQueryString from "modules/hooks/use-query-string";
import { escapeRegExp } from "modules/utils/Functions";

interface IProps {
  html?: string | null;
}

const MessageHtml = ({ html }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowAttachedRef = useRef(false); // prevent re-attaching
  const { qsMessageHighlights } = useQueryString();

  // Prevent XSS attacks
  const sanitizedHtml = useMemo(() => {
    if (!html) return "";
    return DOMPurify.sanitize(html);
  }, [html]);

  // layout effect: the shadow DOM must be filled before paint so the item
  // never paints a collapsed frame between placeholder removal and content
  useLayoutEffect(() => {
    if (!html || !containerRef.current || shadowAttachedRef.current) return;

    const shadowRoot = containerRef.current.attachShadow({ mode: "open" });
    shadowRoot.innerHTML = sanitizedHtml;
    shadowAttachedRef.current = true;

    // Update all links to open in a new tab
    const anchorElements = shadowRoot.querySelectorAll("a");
    anchorElements.forEach((anchor) => {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    });

    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(shadowRoot, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();
    while (currentNode) {
      textNodes.push(currentNode as Text);
      currentNode = walker.nextNode();
    }
    for (const node of textNodes) {
      const parent = node.parentElement;
      if (!node.nodeValue || !parent) continue;

      let replaced = false;
      let html = node.nodeValue;

      qsMessageHighlights.forEach((term) => {
        const regex = new RegExp(`(${escapeRegExp(term)})`, "gi");
        if (regex.test(html)) {
          html = html.replace(
            regex,
            `<span style="background-color: ${colors.yellow};">$1</span>`,
          );
          replaced = true;
        }
      });

      if (replaced) {
        const temp = document.createElement("span");
        temp.innerHTML = html;
        parent.replaceChild(temp, node);
      }
    }
  }, [html, qsMessageHighlights, sanitizedHtml]);

  return <div ref={containerRef} />;
};

export default memo(MessageHtml);
